import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Select, Table, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import SkuListDrawer from './SkuListDrawer';
import { membershipColumns } from './columns';
import { fetchMembershipRules, mapRuleToTableRow } from './data';
import MembershipTableFilters from './MembershipTableFilters';

const { Option } = Select;

const AddIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" {...props}>
    <path d="M7.53125 2.375H8.46875C8.55208 2.375 8.59375 2.41667 8.59375 2.5V13.5C8.59375 13.5833 8.55208 13.625 8.46875 13.625H7.53125C7.44792 13.625 7.40625 13.5833 7.40625 13.5V2.5C7.40625 2.41667 7.44792 2.375 7.53125 2.375Z" fill="white"/>
    <path d="M2.75 7.40625H13.25C13.3333 7.40625 13.375 7.44792 13.375 7.53125V8.46875C13.375 8.55208 13.3333 8.59375 13.25 8.59375H2.75C2.66667 8.59375 2.625 8.55208 2.625 8.46875V7.53125C2.625 7.44792 2.66667 7.40625 2.75 7.40625Z" fill="white"/>
  </svg>
);

const SearchIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" {...props}>
    <path d="M14.2125 13.3516L10.1547 9.29375C10.7844 8.47969 11.125 7.48438 11.125 6.4375C11.125 5.18438 10.6359 4.00937 9.75156 3.12344C8.86719 2.2375 7.68906 1.75 6.4375 1.75C5.18594 1.75 4.00781 2.23906 3.12344 3.12344C2.2375 4.00781 1.75 5.18438 1.75 6.4375C1.75 7.68906 2.23906 8.86719 3.12344 9.75156C4.00781 10.6375 5.18438 11.125 6.4375 11.125C7.48438 11.125 8.47813 10.7844 9.29219 10.1562L13.35 14.2125C13.3619 14.2244 13.376 14.2338 13.3916 14.2403C13.4071 14.2467 13.4238 14.2501 13.4406 14.2501C13.4575 14.2501 13.4741 14.2467 13.4897 14.2403C13.5052 14.2338 13.5194 14.2244 13.5312 14.2125L14.2125 13.5328C14.2244 13.5209 14.2338 13.5068 14.2403 13.4912C14.2467 13.4757 14.2501 13.459 14.2501 13.4422C14.2501 13.4254 14.2467 13.4087 14.2403 13.3931C14.2338 13.3776 14.2244 13.3635 14.2125 13.3516ZM8.9125 8.9125C8.25 9.57344 7.37187 9.9375 6.4375 9.9375C5.50312 9.9375 4.625 9.57344 3.9625 8.9125C3.30156 8.25 2.9375 7.37187 2.9375 6.4375C2.9375 5.50312 3.30156 4.62344 3.9625 3.9625C4.625 3.30156 5.50312 2.9375 6.4375 2.9375C7.37187 2.9375 8.25156 3.3 8.9125 3.9625C9.57344 4.625 9.9375 5.50312 9.9375 6.4375C9.9375 7.37187 9.57344 8.25156 8.9125 8.9125Z" fill="black" fillOpacity="0.88"/>
  </svg>
);

const MECHANIC_PARAM = {
  'Discount + Gift': 'gift_discount',
  Discount: 'discount',
  Gift: 'gift',
};

const MEMBER_TYPE_PARAM = {
  Prime: 'prive',
  Member: 'member',
};

const RULE_TYPE_PARAM = {
  BAU: 'bau',
  'Lifecycle Triggered': 'lifecycle_triggered',
};

const MembershipTable = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [mechanicFilter, setMechanicFilter] = useState('All');
  const [memberTypeFilter, setMemberTypeFilter] = useState('All');
  const [ruleTypeFilter, setRuleTypeFilter] = useState('All');
  const [storeFilter, setStoreFilter] = useState('All');
  const [skuListDrawerVisible, setSkuListDrawerVisible] = useState(false);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, mechanicFilter, memberTypeFilter, ruleTypeFilter, storeFilter]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    const params = { page, limit: pageSize };
    const trimmedSearch = search.trim();
    if (trimmedSearch) params.rule_name = trimmedSearch;
    if (statusFilter !== 'All') params.status = statusFilter.toLowerCase();
    if (mechanicFilter !== 'All') params.benefit_mechanic = MECHANIC_PARAM[mechanicFilter] || mechanicFilter.toLowerCase();
    if (memberTypeFilter !== 'All') params.benefit_type = MEMBER_TYPE_PARAM[memberTypeFilter] || memberTypeFilter.toLowerCase();
    if (ruleTypeFilter !== 'All') params.area = RULE_TYPE_PARAM[ruleTypeFilter] || ruleTypeFilter.toLowerCase();
    if (storeFilter !== 'All') params.storefront = storeFilter;

    const handle = setTimeout(() => {
      fetchMembershipRules(params)
        .then((data) => {
          if (!isMounted) return;
          setRules(Array.isArray(data) ? data : data?.rules || data?.items || []);
          setTotal(Array.isArray(data) ? data.length : data?.pagination?.total ?? 0);
        })
        .catch((err) => {
          message.error(err?.response?.data?.message || 'Failed to load rules');
        })
        .finally(() => {
          if (isMounted) setLoading(false);
        });
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(handle);
    };
  }, [search, statusFilter, mechanicFilter, memberTypeFilter, ruleTypeFilter, storeFilter, page, pageSize]);

  const filteredData = useMemo(() => rules.map(mapRuleToTableRow), [rules]);

  return (
    <div className='MembershipDashboard'>
      <div className='Membership-container-head'>
        <div className='Membership-main-left'>
          <h2 className='main-heading-text'>Membership Rules</h2>
          <span className='main-heading-sub'>Membership Engine / Rules</span>
        </div>

        <div className='Membership-main-right'>
          {/* <Button className='btn-s' onClick={() => setSkuListDrawerVisible(true)}>
            SKU Lists
          </Button> */}
          <Button type="primary" className='btn-p gap-8' onClick={() => {
            navigate('/app/membership-rules/new/create-rules');
          }}>
            <AddIcon />
            New Rule
          </Button>
        </div>
      </div>

      <MembershipTableFilters
        search={search}
        setSearch={setSearch}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        mechanicFilter={mechanicFilter}
        setMechanicFilter={setMechanicFilter}
        memberTypeFilter={memberTypeFilter}
        setMemberTypeFilter={setMemberTypeFilter}
        ruleTypeFilter={ruleTypeFilter}
        setRuleTypeFilter={setRuleTypeFilter}
        storeFilter={storeFilter}
        setStoreFilter={setStoreFilter}
      />

      <Table
        className="membership-table"
        columns={membershipColumns}
        dataSource={filteredData}
        loading={loading}
        pagination={{
          current: page,
          pageSize,
          showSizeChanger: true,
          showQuickJumper: true,
          total,
          showTotal: (t) => `Total ${t} items`,
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPage);
            setPageSize(nextPageSize);
          },
        }}
        scroll={{ x: 1400 }}
        size="middle"
        bordered
        rowClassName={() => 'membership-row'}
      />

      {/* <SkuListDrawer
        visible={skuListDrawerVisible}
        onClose={() => setSkuListDrawerVisible(false)}
      /> */}
    </div>
  );
};

export default MembershipTable;